import { format } from 'date-fns';

const HOUR = 3_600_000;

/** Indices the tick service names differently from the broker. */
const SYMBOL_ALIASES = {
  US500: 'SPXUSD',
  US30: 'DOWUSD',
  NAS100: 'NDXUSD',
  JPN225: 'JPXJPY',
  GER30: 'G30EUR',
  UK100: 'UKXGBP',
  EUSTX50: 'E50EUR',
  HK50: 'HSIHKD',
};

/** "AUDCAD.pro" → "audcad", with the index aliases applied. */
export const tickSymbol = (symbol) => {
  const bare = String(symbol ?? '').split('.')[0].toUpperCase();
  return (SYMBOL_ALIASES[bare] ?? bare).toLowerCase();
};

/** The service wants a local stamp with no zone suffix. */
const localIso = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
};

const shiftHours = (date, hours) => new Date(date.getTime() + hours * HOUR);

/** How many seconds one candle covers on each timeframe tab. */
export const TIMEFRAME_SECONDS = { M5: 300, M15: 900, H1: 3600, H4: 14_400 };

/**
 * The POST body for one trade's candles: its own window padded six hours each
 * side, so there is price context before the entry and after the exit.
 */
export const candleRequest = (trade) => {
  if (!trade?.symbol || !trade.closedAt) return null;
  const opened = trade.openedAt ?? trade.closedAt;
  const closed = trade.closedAt;

  return {
    symbol: tickSymbol(trade.symbol),
    start_date: localIso(shiftHours(opened, -6)),
    end_date: localIso(shiftHours(closed, 6)),
    // Second ticks resolve a short trade; a long one would return far too many.
    timeline: Math.abs(closed - opened) / HOUR <= 48 ? 'one_second' : 'one_minute',
    direction: trade.side === 'Buy' ? 'long' : 'short',
    open_time: localIso(opened),
    close_time: localIso(closed),
    open_price: trade.entry,
    close_price: trade.exit,
  };
};

/** `"2026-09-02T13:13:39+00:00"` → ms. The zone is the broker's own clock. */
const candleTime = (value) => Date.parse(`${String(value ?? '').split('+')[0]}Z`);

/**
 * The service's 1s/1m candles folded into one candle per `seconds` bucket —
 * first open, last close, highest high, lowest low.
 */
export const aggregateCandles = (candles, seconds) => {
  const rows = (Array.isArray(candles) ? candles : [])
    .map((c) => {
      const at = candleTime(c?.time);
      const values = [c?.open, c?.high, c?.low, c?.close].map(Number);
      return Number.isFinite(at) && values.every(Number.isFinite)
        ? { at, open: values[0], high: values[1], low: values[2], close: values[3] }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.at - b.at);

  const size = Math.max(seconds, 1) * 1000;
  const buckets = new Map();
  for (const row of rows) {
    const key = Math.floor(row.at / size) * size;
    const bucket = buckets.get(key);
    if (!bucket) {
      buckets.set(key, { at: key, open: row.open, high: row.high, low: row.low, close: row.close });
    } else {
      bucket.high = Math.max(bucket.high, row.high);
      bucket.low = Math.min(bucket.low, row.low);
      bucket.close = row.close;
    }
  }

  return [...buckets.values()]
    .sort((a, b) => a.at - b.at)
    .map((c) => ({ ...c, time: format(c.at, 'HH:mm') }));
};

/** The candle a moment falls in — the last one that opened at or before it. */
export const indexAt = (rows, when) => {
  if (!when || rows.length === 0) return -1;
  const t = when.getTime();
  let found = -1;
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i].at <= t) found = i;
    else break;
  }
  return found;
};
